import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle, CheckCircle, KeyRound } from 'lucide-react';

const LOGO_LIGHT_BG = "https://customer-assets.emergentagent.com/job_35859c3c-f3c9-45c7-a4c9-9ec823ac8280/artifacts/a7ioyzyi_3%20copy.png";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState('checking'); // checking | ready | submitting | done | invalid
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authAPI.checkResetToken(token);
        if (cancelled) return;
        setEmail(r.data.email);
        setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        setError(e.response?.data?.detail || 'This reset link is invalid or has expired.');
        setPhase('invalid');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pwd.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (pwd !== confirm) { setError('Passwords do not match'); return; }
    setPhase('submitting');
    try {
      await authAPI.resetPassword(token, pwd);
      setPhase('done');
      setTimeout(() => navigate('/login'), 2200);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to reset password');
      setPhase('ready');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-8 space-y-6" data-testid="reset-password-page">
        <div className="text-center">
          <img src={LOGO_LIGHT_BG} alt="KR Agency Hub" className="h-12 mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-semibold text-slate-900">Reset your password</h1>
        </div>

        {phase === 'checking' && (
          <div className="flex items-center justify-center text-slate-500 py-8">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />Validating link…
          </div>
        )}

        {phase === 'invalid' && (
          <>
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription data-testid="reset-invalid-msg">{error}</AlertDescription>
            </Alert>
            <div className="flex justify-center gap-3">
              <Button variant="outline" asChild><Link to="/login">Back to login</Link></Button>
              <Button asChild><Link to="/forgot-password">Request new link</Link></Button>
            </div>
          </>
        )}

        {phase === 'done' && (
          <Alert className="bg-emerald-50 border-emerald-200">
            <CheckCircle className="h-4 w-4 text-emerald-700" />
            <AlertDescription className="text-emerald-900" data-testid="reset-done-msg">
              Password updated. Redirecting to login…
            </AlertDescription>
          </Alert>
        )}

        {(phase === 'ready' || phase === 'submitting') && (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-sm text-slate-600">Choose a new password for <b>{email}</b>.</p>
            {error && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}
            <div className="space-y-2">
              <Label htmlFor="pwd">New password (min. 8 chars)</Label>
              <PasswordInput id="pwd" value={pwd} onChange={(e) => setPwd(e.target.value)}
                     required minLength={8} data-testid="reset-new-pwd" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirm password</Label>
              <PasswordInput id="confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                     required minLength={8} data-testid="reset-confirm-pwd" />
            </div>
            <Button type="submit" className="w-full" disabled={phase === 'submitting'} data-testid="reset-submit-btn">
              {phase === 'submitting' ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Updating…</> : <><KeyRound className="h-4 w-4 mr-2" />Reset password</>}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
